/*
 * Resolves whether the signed-in operator may reach a given nav section's route
 * (admin-ui.md visibility matrix). Shared by every section route — the placeholder
 * (`ProtectedSection`) and each real screen alike — so deep-link protection stays in one
 * place: an operator whose role does not grant the section's permission is redirected to
 * their landing section, and a token that grants no section at all is a dead end.
 */
import { useAdminAuth } from '../session/admin-auth-context';
import { landingSection, NAV_SECTIONS, visibleSections, type NavSection } from './nav-sections';

export type SectionAccess =
  | { readonly kind: 'permitted'; readonly section: NavSection }
  | { readonly kind: 'redirect'; readonly to: string }
  | { readonly kind: 'dead-end' };

/** The signed-in operator's access to `sectionKey`'s route, resolved from their permissions. */
export function useSectionAccess(sectionKey: string): SectionAccess {
  const { claims } = useAdminAuth();
  const permissions = claims?.permissions ?? [];
  const section = NAV_SECTIONS.find((candidate) => candidate.key === sectionKey);
  const permitted =
    section !== undefined &&
    visibleSections(permissions).some((candidate) => candidate.key === section.key);

  if (permitted) {
    return { kind: 'permitted', section };
  }

  const fallback = landingSection(permissions);
  return fallback === null ? { kind: 'dead-end' } : { kind: 'redirect', to: fallback.path };
}
